import { Trans, t } from "@lingui/macro";
import { Copy, Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Soribot, SoribotAPIKey } from "../../models/Soribot";
import { Fire } from "../../services/FireClient";
import { UtilApp } from "../../services/Utils/UtilApp";
import { My } from "../../services/My";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "../../hooks/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";


export const SoribotExposedAPI: React.FC<{ bot: Soribot }> = ({ bot }) => {
  const [keys, setKeys] = useState<SoribotAPIKey[]>([]);
  const { toast } = useToast();
  const [inProgress, setInProgress] = useState(false);
  const [newKeyDialog, setNewKeyDialog] = useState<{ open: boolean, key: string }>({ open: false, key: "" });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ open: boolean, key: SoribotAPIKey | null }>({ open: false, key: null });

  useEffect(() => {
    Fire.bot.getSoribotAPIKeys(bot.id).then(setKeys);
  }, [bot])

  return (
    <div className="space-y-6 max-w-2xl p-4">
      <div className="text-sm space-y-2">
        <p className="font-bold"><Trans>API Endpoint</Trans></p>
        <p className="break-all font-mono bg-gray-50 p-2 rounded border">https://us-central1-nextpod-dev.cloudfunctions.net/api/v1/ask</p>
      </div>

      <div className="text-sm space-y-2">
        <p className="font-bold"><Trans>Example body (JSON)</Trans></p>
        <Textarea
          readOnly
          className="font-mono text-xs h-32 bg-gray-50"
          value={`{\n  "soribotId": "${bot.id}",\n  "prompt": "Hello",\n  "userId": "User identifier"\n}`}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="font-bold"><Trans>API Keys</Trans></p>
          <Button size="sm" onClick={() => createNewAPIKey("")}>
            <Plus className="w-4 h-4 mr-2" /> <Trans>Create new key</Trans>
          </Button>
        </div>

        {keys.length === 0 && <div className="p-4 text-center text-gray-500 border rounded border-dashed"><Trans>No API keys found.</Trans></div>}

        <div className="space-y-2">
          {keys.map((k, i) => (
            <div key={i} className="flex items-center justify-between p-3 border rounded bg-white">
              <div>
                <div className="font-mono font-bold">{k.title || k.key?.substring(0, 8) || t`Unnamed key`}...</div>
                <div className="text-xs text-gray-500">
                  <Trans>Created</Trans>: {UtilApp.dateStrToTimeAndDay(k.created, My.user.lang)}
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => confirmDelete(k)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {inProgress && <div className="flex justify-center p-4"><Loader2 className="animate-spin" /></div>}

      {/* New Key Dialog */}
      <Dialog open={newKeyDialog.open} onOpenChange={(open) => setNewKeyDialog(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><Trans>New API Key</Trans></DialogTitle>
            <DialogDescription>
              <Trans>Please copy the key and store it in a safe place. You will not be able to see the key again.</Trans>
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center space-x-2 mt-4">
            <div className="grid flex-1 gap-2">
              <Label htmlFor="link" className="sr-only">Link</Label>
              <Input id="link" defaultValue={newKeyDialog.key} readOnly />
            </div>
            <Button size="icon" className="px-3" onClick={() => {
              navigator.clipboard.writeText(newKeyDialog.key);
              toast({ description: t`Copied to clipboard` });
            }}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setNewKeyDialog({ open: false, key: "" })}><Trans>Close</Trans></Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmDialog.open} onOpenChange={(open) => setDeleteConfirmDialog(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><Trans>Delete API Key</Trans></AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>Are you sure you want to delete this API Key?</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction onClick={executeDelete} className="bg-red-500 hover:bg-red-600"><Trans>Delete</Trans></AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );

  function confirmDelete(key: SoribotAPIKey) {
    setDeleteConfirmDialog({ open: true, key });
  }

  function executeDelete() {
    if (deleteConfirmDialog.key) {
      deleteKey(deleteConfirmDialog.key);
      setDeleteConfirmDialog({ open: false, key: null });
    }
  }

  async function createNewAPIKey(name: string) {
    setInProgress(true);
    try {
      const key = await Fire.bot.createSoribotAPIKey(bot.id);
      setKeys(await Fire.bot.getSoribotAPIKeys(bot.id));
      setNewKeyDialog({ open: true, key: key });
    } catch (error: any) {
      toast({ variant: "destructive", description: error.message });
    } finally {
      setInProgress(false);
    }
  }

  function deleteKey(k: SoribotAPIKey) {
    Fire.bot.deleteSoribotAPIKey(bot.id, k.id).then(async () => {
      setKeys(await Fire.bot.getSoribotAPIKeys(bot.id));
      toast({ description: t`API Key deleted` });
    }).catch((error: any) => {
      toast({ variant: "destructive", description: error.message });
    });
  }
}
export default SoribotExposedAPI;